import { OfflineApiService } from './services/offline-api.service';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  TemplateRef,
  ViewChild,
  signal,
  OnInit,
} from '@angular/core';
import { APIService } from './services/api.service';
import { Router } from '@angular/router';
import { LoggingService } from './services/logging.service';
import { NzI18nInterface, NzI18nService } from 'ng-zorro-antd/i18n';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { environment } from '../environments/environment';
import { EventService } from './pages/schedule/event/event.service';
import { PermissionsService, Entities, Permissions } from './services/permissions.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { from, fromEvent, interval, Observable, of, tap } from 'rxjs';
import { UserService } from './services/user.service';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { AmplifyService } from './services/amplify.service';
import { StateService } from './services/state.service';
import { ContactsService } from './pages/contacts/contacts.service';
import { UtilitiesService } from './services/utilities.service';
import { CredentialsService } from './services/webauthn/credentials.service';
import { BehaviorSubject } from 'rxjs';
import { Hub, I18n } from 'aws-amplify/utils';
import { translations } from '@aws-amplify/ui';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import MemoryAdapter from './services/webauthn/memory-adapter.service';
import { SpeedTestService } from 'ng-speed-test';
import { TaskService } from './pages/schedule/task/task.service';
import * as Sentry from '@sentry/angular-ivy';
import { confirmResetPassword, signIn } from 'aws-amplify/auth';

const userCredentialsSubject = new BehaviorSubject<{
  username: string;
  password: string;
} | null>(null);

const biometricEnableSubject = new BehaviorSubject<boolean | null>(null);

export type Notification = {
  to: string;
  message: {
    value: string;
    value_es: string;
    value_fr: string;
  };
  date: number;
  level: string;
  read: boolean;
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild('notificationTemplate') public notificationTemplate!: TemplateRef<any>;
  private pwaNotifShown = false;

  private checkSessionTimeout: any;
  public isProd = environment.env === 'prod';
  public isDev = environment.env === 'dev';
  public isStaging = environment.env === 'staging';
  public isSandbox = environment.env === 'sandbox';

  public showConsole = false;

  public serviceWorkerActive = false;

  public envClass =
    environment.env === 'local' || environment.env === 'dev'
      ? 'dev-env'
      : environment.env === 'staging'
        ? 'staging-env'
        : environment.env === 'sandbox'
          ? 'sandbox-env'
          : 'prod-env';

  public newMessagesCount = 0;
  public eventsCount = 0;

  public locale: NzI18nInterface = this.i18n.getLocale();

  public loading = false;
  public hasHomeMenu = false;
  public hasScheduleMenu = false;
  public hasMessagesMenu = false;
  public hasOfflineMenu = false;
  public hasProgramDataMenu = false;
  public hasNewProgramMapMenu = false;
  public hasFormsMenu = false;
  public hasReportsMenu = false;
  public hasAccountingMenu = false;
  public hasFeedbackMenu = false;
  public hasMediasMenu = false;
  public hasNewsMenu = false;
  public hasAdministrationMenu = false;
  public hasInvestmentProjectsMenu = false;
  public hasParametersMenu = false;
  public hasPropertiesMenu = false;
  public hasDomainValuesMenu = false;
  public hasProgramsMenu = false;
  public hasDashboardMenu = false;
  public hasFormBuilderMenu = false;
  public hasSecurityMenu = false;
  public hasPermissionsMenu = false;
  public hasUsersMenu = false;
  public hasContactsMenu = false;
  public hasInvestmentMapsMenu = false;
  public hasNotificationsMenu = false;
  public hasAccountRequestsMenu = false;
  public hasBusinessIntelligenceMenu = false;
  public version = environment.version + ' (' + environment.env + ')';
  public canSeeConsole = false;
  public biometricEnable = false;
  public biometricActivated = false;
  public drawerVisible = false;
  public notificationVisible = false;
  public tasksCountForTodaySubject = new BehaviorSubject<number>(0);
  public tasksCountForToday$ = this.tasksCountForTodaySubject.asObservable();
  public tasksCountObservable$!: Observable<void>;

  public notifications$ = new Observable<Notification[]>();

  private readonly onlineEvent: Observable<Event>;
  private readonly offlineEvent: Observable<Event>;
  private readonly visibilityEvent: Observable<Event>;
  private readonly networkChangeEvent: Observable<Event> | null = null;
  private thirtySecondsInterval: any;
  private eventsInterval: any;
  // private readonly syncWorker: any = undefined;
  public memoryUsed = 0;
  public memoryLimit = 0;
  public isPwaInstalled = false;
  private pwaInterval: any = undefined;
  public hasNewNotifications = 0;

  public formFields = {
    signIn: {
      username: {
        labelHidden: false,
        placeholder: $localize`Enter your email`,
        isRequired: true,
      },
      password: {
        labelHidden: false,
        placeholder: $localize`Enter your password`,
        isRequired: true,
      },
    },
  };

  public readonly networkSpeedValues: Array<number> = [];
  private memoryInterval: any;
  public networkSpeedInterval: any;
  public navigatorProperties: any;
  public storageQuota = 0;
  public storageUsage = 0;
  private userLoadedSubject = new BehaviorSubject<boolean>(false);

  constructor(
    public readonly router: Router,
    public readonly apiService: APIService,
    public readonly offlineApiService: OfflineApiService,
    public readonly logger: LoggingService,
    private readonly updates: SwUpdate,
    private readonly ref: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly i18n: NzI18nService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly userService: UserService,
    private readonly eventService: EventService,
    private readonly permissionsService: PermissionsService,
    private readonly notification: NzNotificationService,
    private readonly amplifyService: AmplifyService,
    private readonly stateService: StateService,
    private readonly contactsService: ContactsService,
    public readonly utilitiesService: UtilitiesService,
    private readonly credentialsService: CredentialsService,
    public readonly authenticatorService: AuthenticatorService,
    private readonly memoryAdapter: MemoryAdapter,
    private readonly speedTestService: SpeedTestService,
    private readonly taskService: TaskService,
  ) {
    try {
      if (!structuredClone) {
        // this will fail on older browsers
      }
    } catch (error) {
      this.logger.error(new Error('Invalid browser version'), false);
      const message = $localize`The version of your browser is not up-to-date. You could have problems with this application. Please update your browser to the latest version.`;
      this.notification.create('warning', $localize`Warning!`, message, {
        nzPlacement: 'top',
        nzDuration: 30000,
      });
    }

    this.onlineEvent = fromEvent(window, 'online');
    this.offlineEvent = fromEvent(window, 'offline');
    this.visibilityEvent = fromEvent(window.document, 'visibilitychange');
    if ((navigator as any).connection) {
      this.networkChangeEvent = fromEvent((navigator as any).connection, 'change');
    }

    this.navigatorProperties = window.navigator;

    // will come back to this later
    /* if (typeof Worker !== 'undefined') {
      this.syncWorker = new Worker(new URL('./offline.worker', import.meta.url));
      // @ts-ignore
      this.syncWorker.onmessage = ({ data }): void => {
        this.logger.info(`page got message: ${data}`);
      };
    } else {
      this.logger.warn('Web worker not supported in this browser');
    }*/
  }

  public services = {
    async handleSignIn(formData: Record<string, any>): Promise<any> {
      let { username, password } = formData;
      username = username.trim();
      password = password.trim();

      userCredentialsSubject.next({ username, password });

      return signIn({
        username,
        password,
      });
    },

    async handleForgotPasswordSubmit(formData: Record<string, any>): Promise<any> {
      const { username, code, password } = formData;
      biometricEnableSubject.next(false);
      return confirmResetPassword({ username, confirmationCode: code, newPassword: password });
    },
  };

  public async ngOnInit(): Promise<void> {
    this.userLoadedSubject
      .asObservable()
      .pipe(
        filter((res) => res),
        take(1),
        switchMap(() => this.userService.setCurrentUser((this.apiService.user as any)?.attributes?.email)),
        switchMap(() => this.contactsService.setCurrentContact((this.apiService.user as any)?.attributes?.email)),
      )
      .subscribe({
        next: (res) => {
          this.logger.warn('User loaded!');
          this.allActionsThatNeedAuth();
        },
        error: (error) => {
          this.logger.error(error);
        }
      });
    Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          this.logger.info('user has been signedIn successfully.');
          this.checkAuthStatus();
          break;
        case 'signedOut':
          Sentry.captureMessage('Hub: App signOut', {
            tags: {
              online: navigator.onLine,
              networkSpeed: (navigator as any).connection?.downlink,
              rtt: (navigator as any).connection?.rtt,
              saveData: (navigator as any).connection?.saveData,
              networkType: (navigator as any).connection?.type,
              networkEffectiveType: (navigator as any).connection?.effectiveType,
              version: environment.version,
            },
          });
          this.logger.info('user has been signedOut successfully.');
          break;
        case 'tokenRefresh':
          this.logger.info('auth tokens have been refreshed.');
          break;
        case 'tokenRefresh_failure':
          this.logger.error('failure while refreshing auth tokens.', false);
          break;
        case 'signInWithRedirect':
          this.logger.info('signInWithRedirect API has successfully been resolved.');
          break;
        case 'signInWithRedirect_failure':
          this.logger.error('failure while trying to resolve signInWithRedirect API.', false);
          break;
        case 'customOAuthState':
          this.logger.info('custom state returned from CognitoHosted UI');
          break;
      }
    });
    if (navigator.storage && navigator.storage.estimate) {
      const estimation = await navigator.storage.estimate();
      if (estimation && estimation.quota && estimation.usage) {
        this.storageUsage = estimation.usage;
        this.storageQuota = estimation.quota;
      }
    } else {
      this.logger.error('StorageManager not found', false);
    }
    this.isPwaInstalled = window.matchMedia('(display-mode: standalone)').matches;
    this.loading = true;
    this.logger.debug('locale', this.locale);
    this.logger.info('language', navigator.language);
    if (!this.locale.locale) {
      this.locale.locale = 'en';
    }
    if (!this.isPwaInstalled) {
      if (!navigator.language) {
        localStorage.setItem('USER_LOCALE', this.locale.locale);
      } else {
        if (window.location.protocol === 'https:') {
          if (!localStorage.getItem('USER_LOCALE')) {
            let navigatorLanguage = navigator.language;
            if (navigatorLanguage.indexOf('-') !== -1) {
              navigatorLanguage = navigatorLanguage.substring(0, navigatorLanguage.indexOf('-'));
            }
            if (this.locale.locale !== navigatorLanguage) {
              this.locale.locale = navigatorLanguage;
              this.switchLanguage();
            } else {
              localStorage.setItem('USER_LOCALE', this.locale.locale);
            }
          } else {
            if (this.locale.locale !== localStorage.getItem('USER_LOCALE')) {
              this.locale.locale = localStorage.getItem('USER_LOCALE') ?? this.locale.locale;
              this.switchLanguage();
            } else {
              localStorage.setItem('USER_LOCALE', this.locale.locale);
            }
          }
        }
      }
    }

    I18n.setLanguage(this.locale.locale);
    I18n.putVocabularies(translations);

    await this.memoryAdapter.createKey();
    const user = this.memoryAdapter.getValue('user');
    this.biometricActivated = !!this.memoryAdapter.getValue(`pwd-${user}`);
    this.biometricEnable = !!this.memoryAdapter.getValue('biometric');

    this.utilitiesService.innerScreenWidth = window.innerWidth;

    userCredentialsSubject
      .asObservable()
      .pipe(filter((cred) => !!cred))
      .subscribe({
        next: (cred) => {
          if (cred) {
            this.biometricActivated = true;
            this.credentialsService.setUserCredential(cred);
          }
        },
        error: (error) => {
          this.logger.error(error);
        }
      });
    biometricEnableSubject
      .asObservable()
      .pipe(filter((biometric) => biometric !== null))
      .subscribe({
        next: (biometric) => {
          this.biometricEnable = !!biometric;
          this.biometricActivated = !!this.memoryAdapter.getValue(`pwd-${user}`);
        },
        error: (error) => {
          this.logger.error(error);
        }
      });

    if (this.apiService.networkInformation) {
      if (navigator.onLine && this.apiService.networkInformation?.downlink < Number(environment.DOWNLINK_TRESHOLD)) {
        this.apiService.onLine = false;
        this.logger.onLine = false;
        this.userService.onLine = false;
        document.body.style.overscrollBehaviorY = 'contain';
      } else if (
        navigator.onLine &&
        this.apiService.networkInformation?.downlink >= Number(environment.DOWNLINK_TRESHOLD)
      ) {
        this.apiService.onLine = true;
        this.logger.onLine = true;
        this.userService.onLine = true;
        document.body.style.overscrollBehaviorY = 'auto';
      }
    }

    if (this.apiService.onLine) {
      await this.checkAuthStatus();
    } else {
      if (localStorage.getItem('decodedToken')) {
        this.userService.decodedToken = JSON.parse(localStorage.getItem('decodedToken') ?? '');
      }
      if (localStorage.getItem('authData')) {
        this.apiService.user = JSON.parse(localStorage.getItem('authData') ?? '');
        Sentry.setUser(this.apiService.user || null);
      }
      if (localStorage.getItem('userIdentityId')) {
        this.apiService.userIdentityId = localStorage.getItem('userIdentityId') ?? '';
      }
      this.userLoadedSubject.next(true);
    }

    this.breakpointObserver.observe(['(max-width: 550px)']).subscribe((state: BreakpointState) => {
      this.apiService.isExtraSmallScreen = state.breakpoints['(max-width: 550px)'];
    });

    this.breakpointObserver.observe(['(max-width: 600px)']).subscribe((state: BreakpointState) => {
      this.apiService.isSmallerScreen = state.breakpoints['(max-width: 600px)'];
    });

    this.breakpointObserver.observe(['(max-width: 768px)']).subscribe((state: BreakpointState) => {
      this.apiService.isSmallScreen = state.breakpoints['(max-width: 768px)'];
    });

    this.breakpointObserver.observe(['(max-width: 960px)']).subscribe((state: BreakpointState) => {
      this.apiService.isMediumScreen = state.breakpoints['(max-width: 960px)'];
    });

    this.breakpointObserver.observe(['(min-width: 960px)']).subscribe((state: BreakpointState) => {
      this.apiService.isLargeScreen = state.breakpoints['(min-width: 960px)'];
    });

    this.breakpointObserver.observe(['(min-width: 1280px)']).subscribe((state: BreakpointState) => {
      this.apiService.isVeryLargeScreen = state.breakpoints['(min-width: 1280px)'];
    });

    this.breakpointObserver.observe(['(min-width: 2000px)']).subscribe((state: BreakpointState) => {
      this.apiService.isXLScreen = state.breakpoints['(min-width: 2000px)'];
    });
  }

  private async allActionsThatNeedAuth(): Promise<void> {
    this.onlineEvent.subscribe(() => {
      if (this.apiService.networkInformation?.downlink < Number(environment.DOWNLINK_TRESHOLD)) {
        this.apiService.onLine = false;
        this.logger.onLine = false;
        this.userService.onLine = false;
        document.body.style.overscrollBehaviorY = 'contain';
      } else {
        this.apiService.onLine = true;
        this.logger.onLine = true;
        this.userService.onLine = true;
        document.body.style.overscrollBehaviorY = 'auto';
        this.backOnline();
      }
      if (this.apiService.onLine) {
        this.checkPWA();
      }
    });

    this.offlineEvent.subscribe(() => {
      this.apiService.onLine = false;
      this.logger.onLine = false;
      this.userService.onLine = false;
      document.body.style.overscrollBehaviorY = 'contain';
    });

    this.visibilityEvent.subscribe(() => {
      if (!document.hidden) {
        setTimeout(() => {
          this.amplifyService.refreshSession().then((authSession) => {
            this.logger.warn('refreshed authSession', authSession);
          });
        }, 500);
      }
    });

    if (this.networkChangeEvent) {
      this.networkChangeEvent.subscribe(() => {
        this.apiService.networkInformation = (navigator as any).connection;
        this.logger.info('network change', (navigator as any).connection);
        if (this.apiService.networkInformation) {
          const previous = this.apiService.onLine;
          if (
            navigator.onLine &&
            this.apiService.networkInformation?.downlink < Number(environment.DOWNLINK_TRESHOLD)
          ) {
            this.apiService.onLine = false;
            this.logger.onLine = false;
            this.userService.onLine = false;
            document.body.style.overscrollBehaviorY = 'contain';
          } else if (
            navigator.onLine &&
            this.apiService.networkInformation?.downlink >= Number(environment.DOWNLINK_TRESHOLD)
          ) {
            this.apiService.onLine = true;
            this.logger.onLine = true;
            this.userService.onLine = true;
            document.body.style.overscrollBehaviorY = 'auto';
            if (!previous) {
              this.backOnline();
            }
          }
        }
      });
    }
    setTimeout(() => {
      if (this.authenticatorService.authStatus === 'authenticated' && this.apiService.onLine) {
        this.fetchNotifications();
      }

      if (this.thirtySecondsInterval) {
        clearInterval(this.thirtySecondsInterval);
        this.thirtySecondsInterval = null;
      }
      this.thirtySecondsInterval = setInterval(() => {
        if (this.authenticatorService.authStatus === 'authenticated' && this.apiService.onLine && !document.hidden) {
          this.fetchNewMessagesCount();
        }
      }, 30000);
      if (this.eventsInterval) {
        clearInterval(this.eventsInterval);
        this.eventsInterval = null;
      }
      this.eventsInterval = setInterval(() => {
        this.amplifyService.refreshSession().then((authSession) => {
          this.logger.warn('refreshed authSession', authSession);
          if (authSession) {
            if (
              this.authenticatorService.authStatus === 'authenticated' &&
              this.apiService.onLine &&
              !document.hidden
            ) {
              this.eventService.getCountForToday().then((result: number) => (this.eventsCount = result));
              this.tasksCountObservable$ = this.taskService
                .tasksFor(new Date(Date.now()))
                .pipe(map((tasks) => this.tasksCountForTodaySubject.next(tasks.length)));
            }
          }
        });
      }, 600000);
      this.checkPWA();
    }, 20000);
    this.stateService.formDefinitionsLoaded$.pipe(
      filter((x) => x),
      take(1),
    ).subscribe({
      next: (res) => {
        if (res) {
          setTimeout(() => {
            if (this.apiService.onLine && this.router.url !== '/offline') {
              this.offlineApiService.syncOfflineData();
            }
          }, 60000);
          // this.syncWorker?.postMessage('hello');
        }
      },
      error: (error) => {
        this.logger.error(error);
      }
    });
    this.contactsService.currentContact$.pipe(take(1)).subscribe({
      next: (contact) => {
        if (contact) {
          // preload some popular domVals
          this.stateService.getDomainValue('units').catch((error) => {
            this.logger.error(error);
          });
          this.stateService.getDomainValue('contactType').catch((error) => {
            this.logger.error(error);
          });
          this.stateService.getDomainValue('countries').catch((error) => {
            this.logger.error(error);
          });
          this.stateService.getDomainValue('role').catch((error) => {
            this.logger.error(error);
          });
          this.stateService.getDomainValue('shapeType').catch((error) => {
            this.logger.error(error);
          });
          this.canSeeConsole =
            contact.contactRoles.indexOf('AdministratorsRole') !== -1 ||
            contact.contactRoles.indexOf('PilotsRole') !== -1;
        }
      },
      error: (error) => {
        this.logger.error(error);
      }
    });
    await this.loadGlobalData();
  }

  private backOnline(): void {
    setTimeout(() => {
      this.checkAuthStatus()
        .then(() => {})
        .catch((error) => {
          this.logger.error(error, false);
        });
    }, 100);
    setTimeout(() => {
      // wait 30 sec before syncing to allow other operations to proceed before if any
      if (this.router.url !== '/offline') {
        this.offlineApiService
          .syncOfflineData()
          .then(() => {
            this.logger.info('syncOfflineData done');
          })
          .catch((error) => {
            this.logger.error(error);
          });
      }
    }, 30000);
  }

  @HostListener('window:resize', ['$event'])
  public onResolve(): void {
    this.utilitiesService.innerScreenWidth = window.innerWidth;
  }

  public async activateBiometric(): Promise<void> {
    try {
      const user = await this.credentialsService.getUserCredential();
      if (!user) {
        throw new Error('User not found');
      }
      this.credentialsService
        .registerUser({
          name: user.username,
          username: user.username,
          password: user.password,
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            if (response) {
              if (response.status === 'ok') {
                this.biometricEnable = true;
              } else {
                this.logger.error(new Error(response.message));
              }
            } else {
              this.logger.error(new Error('Invalid response from registerUser'));
            }
          },
          error: (error) => {
            this.logger.error(error);
          }
        });
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async biometricLogin(): Promise<void> {
    const user = this.memoryAdapter.getValue('user') ?? '';
    const userCred = await this.memoryAdapter.getCred();

    this.credentialsService
      .loginUser(user)
      .pipe(
        take(1),
        catchError((error) => {
          this.logger.error(error, false);
          this.checkAuthStatus();
          return of(null);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response?.status === 'ok') {
            signIn({ username: userCred?.username ?? '', password: userCred?.password ?? '' })
              .then(() => {
                // no need to call checkAuthStatus because Hub is listening to signedIn event.
              })
              .catch((error) => {
                this.logger.error(error, false);
                this.checkAuthStatus();
              });
          } else {
            this.checkAuthStatus();
          }
        },
        error: (error) => {
          this.logger.error(error);
        }
      });
  }

  private async checkAuthStatus(): Promise<void> {
    this.logger.warn('authStatus', this.authenticatorService.authStatus);
    if (this.authenticatorService.authStatus === 'authenticated') {
      await this.amplifyService.refreshSession();
      // there is a subscribe - take(1) on this subject to load global data
      this.userLoadedSubject.next(true);
    } else if (this.authenticatorService.authStatus === 'configuring') {
      // TODO find a way to listen on this.authenticatorService.authStatus instead
      if (this.checkSessionTimeout) {
        clearTimeout(this.checkSessionTimeout);
      }
      this.checkSessionTimeout = setTimeout(() => {
        this.checkAuthStatus();
      }, 1000);
    }
    this.ngZone.run(() => {
      this.ref.detectChanges();
    });
  }

  private async loadGlobalData(): Promise<void> {
    this.logger.info('loadGlobalData');
    this.stateService.fetchGlobalData();
    await this.loadUserAccess();
    if (this.apiService.onLine) {
      setTimeout(() => {
        this.fetchNewMessagesCount();
        this.fetchNotifications();
        this.taskService
          .tasksFor(new Date(Date.now()))
          .subscribe({
            next: (tasks) => this.tasksCountForTodaySubject.next(tasks.length),
            error: (error) => {
              this.logger.error(error);
            }
          });
        this.eventService
          .getCountForToday()
          .then((result: number) => (this.eventsCount = result))
          .catch((error) => this.logger.error(error));
      }, 10000);
    }
    if (this.router.url === '/') {
      this.ngZone.run(() => {
        this.router.navigate(['/welcome']);
      });
    }
  }

  private checkPWA(): void {
    this.logger.info('checkPWA');
    if (this.updates.isEnabled) {
      this.logger.info('serviceworker updates enabled');
      this.serviceWorkerActive = true;
      this.updates.unrecoverable.subscribe({
        next: (event) => {
          this.logger.error(event);
          if (navigator.onLine) {
            this.utilitiesService.reloadApp();
          }
        },
        error: (error) => {
          this.logger.error(error);
        }
      });
      this.updates.versionUpdates
        .pipe(
          tap((evt) => {
            this.logger.info('versionUpdates', JSON.stringify(evt));
            if (evt.type === 'VERSION_INSTALLATION_FAILED') {
              this.logger.error(JSON.stringify(evt), false);
            }
          }),
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        )
        .subscribe({
          next: () => {
            if (!this.pwaNotifShown && navigator.onLine) {
              this.pwaNotifShown = true;
              setTimeout(() => {
                this.pwaNotifShown = false;
              }, 601000);
              this.updates
                .activateUpdate()
                .then((res) => {
                  if (res) {
                    this.logger.info('activateUpdate() success');
                  } else {
                    this.logger.error(new Error('activateUpdate() failed'));
                  }
                  this.notification.template(this.notificationTemplate, { nzDuration: 600000 });
                })
                .catch((error) => {
                  this.logger.error(error);
                  this.pwaNotifShown = false;
                });
            }
          },
          error: (e: unknown) => this.logger.error(e),
          complete: () => this.logger.info('versionUpdates subscribe complete'),
        });
      this.logger.info('Setting PWA update interval');
      if (this.pwaInterval) {
        clearInterval(this.pwaInterval);
      }
      this.pwaInterval = setInterval(async () => {
        if (this.apiService.onLine && !document.hidden) {
          try {
            this.logger.info('checking for updates');
            this.updates
              .checkForUpdate()
              .then((result) => {
                this.logger.info('checkForUpdates', result);
              })
              .catch((error) => {
                this.logger.error(error);
              });
          } catch (error) {
            this.logger.error(error);
          }
        }
      }, 60000);
    } else {
      if (environment.env === 'prod' || environment.env === 'staging' || environment.env === 'dev') {
        this.logger.error(new Error('Service worker updates are disabled.'), false);
      }
      this.serviceWorkerActive = false;
    }
  }

  public installAppNewVersion(): void {
    Sentry.captureMessage('Installing new version', {
      tags: {
        online: navigator.onLine,
        networkSpeed: (navigator as any).connection?.downlink,
        rtt: (navigator as any).connection?.rtt,
        saveData: (navigator as any).connection?.saveData,
        networkType: (navigator as any).connection?.type,
        networkEffectiveType: (navigator as any).connection?.effectiveType,
        version: environment.version,
      },
    });
    this.utilitiesService.reloadApp();
  }

  private async loadUserAccess(): Promise<void> {
    try {
      this.hasScheduleMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Schedules);
      this.hasMessagesMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Messages);
      this.hasOfflineMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Offline);

      this.hasHomeMenu = this.hasScheduleMenu || this.hasMessagesMenu;

      this.hasNewProgramMapMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Map);
      this.hasFormsMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Forms);
      this.hasReportsMenu = await this.permissionsService.userCan(Permissions.Read, Entities.ReportsList);
      this.hasBusinessIntelligenceMenu = await this.permissionsService.userCan(
        Permissions.Read,
        Entities.BusinessIntelligences,
      );
      this.hasAccountingMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Accounting);
      this.hasProgramDataMenu = this.hasNewProgramMapMenu || this.hasFormsMenu;
      this.hasInvestmentMapsMenu =
        this.hasNewProgramMapMenu &&
        (await this.permissionsService.userCan(Permissions.Read, Entities.InvestmentProjects));
      this.hasContactsMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Contacts);

      this.hasMediasMenu = await this.permissionsService.userCan(Permissions.Read, Entities.MediaBundles);
      this.hasNewsMenu = await this.permissionsService.userCan(Permissions.Read, Entities.NewsList);
      this.hasFeedbackMenu = this.hasMediasMenu || this.hasNewsMenu;

      this.hasDomainValuesMenu = await this.permissionsService.userCan(Permissions.Read, Entities.DomainValues);
      this.hasParametersMenu = this.hasDomainValuesMenu;
      this.hasProgramsMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Programs);
      this.hasDashboardMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Dashboards);
      this.hasPropertiesMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Properties);
      this.hasFormBuilderMenu = await this.permissionsService.userCan(Permissions.Read, Entities.FormBuilder);
      this.hasUsersMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Users);
      this.hasPermissionsMenu = await this.permissionsService.userCan(Permissions.Read, Entities.Permissions);
      this.hasAccountRequestsMenu = await this.permissionsService.userCan(Permissions.Read, Entities.AccountRequests);
      this.hasSecurityMenu = this.hasPermissionsMenu || this.hasUsersMenu || this.hasAccountRequestsMenu;
      this.hasInvestmentProjectsMenu = await this.permissionsService.userCan(
        Permissions.Read,
        Entities.InvestmentProjects,
      );
      this.hasNotificationsMenu = await this.permissionsService.userCan(
        Permissions.Read,
        Entities.ProjectNotifications,
      );
      this.hasAdministrationMenu =
        this.hasParametersMenu || this.hasFormBuilderMenu || this.hasSecurityMenu || this.hasInvestmentProjectsMenu;
    } catch (error) {
      this.logger.error(error);
    }
    this.loading = false;
  }

  private fetchNewMessagesCount(): void {
    if (!this.apiService.onLine) {
      return;
    }
    this.apiService
      .getCollection({
        collection: 'messages',
        sort: 'date',
        sortOrder: 'desc',
        filters: [
          {
            field: 'to',
            value: (this.apiService.user as any)?.attributes?.email,
            operator: 'eq',
          },
          {
            field: 'read',
            value: false,
            operator: 'eq',
          },
        ],
        returnCount: true,
        limit: 100,
      })
      .then((messageCount) => {
        this.newMessagesCount = messageCount;
        this.ngZone.run(() => {
          this.ref.detectChanges();
        });
      })
      .catch((error) => {
        this.logger.error(error);
      });
  }

  public toggleCollapse(): void {
    this.apiService.isMenuCollapse = !this.apiService.isMenuCollapse;
  }

  public async signOut(): Promise<void> {
    await this.amplifyService.signOut(true);
  }

  public redirectTo(event: Event, uri: string): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.apiService.isSmallScreen) {
      this.toggleCollapse();
    }
    this.ngZone.run(() => {
      this.router
        .navigateByUrl(uri)
        .then()
        .catch((error) => {
          this.logger.error(error);
        });
    });
  }

  public switchLanguage(): void {
    this.logger.debug(window.location);
    this.logger.debug('locale', this.locale.locale);
    localStorage.setItem('USER_LOCALE', this.locale.locale);

    if (this.locale.locale === 'es') {
      window.location.replace('https://' + window.location.host + '/es');
    } else if (this.locale.locale === 'en') {
      window.location.replace('https://' + window.location.host + '/en');
    } else {
      window.location.replace('https://' + window.location.host + '/fr');
    }
  }

  public showDrawer(): void {
    this.networkSpeedValues.length = 0;
    this.startMemoryInterval();
    this.drawerVisible = true;
  }

  public closeDrawer(): void {
    this.networkSpeedValues.length = 0;
    clearInterval(this.memoryInterval);
    this.memoryInterval = null;
    clearInterval(this.networkSpeedInterval);
    this.networkSpeedInterval = null;
    this.drawerVisible = false;
  }

  public networkSpeedIntervalCount = 0;
  private networkSpeedTestRunning = false;

  private startMemoryInterval(): void {
    this.memoryInterval = setInterval(() => {
      if (this.drawerVisible) {
        if ((window.performance as any).memory) {
          this.memoryLimit = Math.round((window.performance as any).memory.jsHeapSizeLimit / 1024 / 1000);
          this.memoryUsed = Math.round((window.performance as any).memory.usedJSHeapSize / 1024 / 1000);
        }
      }
    }, 3000);
  }

  public startNetworkSpeedInterval(): void {
    this.networkSpeedIntervalCount = 0;
    this.networkSpeedTestRunning = false;
    this.networkSpeedInterval = setInterval(() => {
      if (this.drawerVisible) {
        try {
          if (this.networkSpeedIntervalCount > 5) {
            clearInterval(this.networkSpeedInterval);
            this.networkSpeedInterval = null;
            this.networkSpeedTestRunning = false;
          }
          if (this.networkSpeedTestRunning) {
            return;
          }
          this.networkSpeedIntervalCount++;
          this.networkSpeedTestRunning = true;
          this.logger.warn('calling getMbps()');
          this.speedTestService
            .getMbps()
            .pipe(take(1))
            .subscribe({
              next: (speed) => {
                this.logger.warn('got getMbps()');
                if (this.networkSpeedValues.unshift(speed) > 5) {
                  this.networkSpeedValues.pop();
                }
                this.networkSpeedTestRunning = false;
              },
              error: (error: unknown) => {
                this.logger.error(error);
                this.networkSpeedTestRunning = false;
              },
            });
        } catch (error) {
          this.logger.error(error);
        }
      }
    }, 4000);
  }

  public stopNetworkSpeedInterval(): void {
    clearInterval(this.networkSpeedInterval);
    this.networkSpeedInterval = null;
    this.networkSpeedTestRunning = false;
  }

  public checkSentry(): void {
    throw new Error('Sentry global error handler check from ' + this.apiService.user?.attributes?.email);
  }

  public gotoMessages(): void {
    this.router.navigate(['/messages/inbox']).catch((error) => {
      this.logger.error(error, false);
    });
  }

  public readonly notificationsVisible = signal<boolean>(false);

  public showNotifications(): void {
    this.notificationVisible = true;
    this.notificationsVisible.update(() => true);
    this.apiService.markNotificationsAsRead(this.apiService.user?.attributes?.email).catch((error) => {
      this.logger.error(error, false);
    });
  }

  public hideNotifications(): void {
    this.notificationVisible = false;
    this.notificationsVisible.update(() => false);
  }

  private fetchNotifications(): void {
    const filters = [
      {
        field: 'to',
        value: (this.apiService.user as any)?.attributes?.email,
        operator: 'eq',
      },
    ];

    this.notifications$ = interval(300000).pipe(
      switchMap(() => {
        if (this.apiService.onLine) {
          return from(
            this.apiService.getCollection({
              collection: 'notifications',
              sort: 'date',
              sortOrder: 'desc',
              filters: filters,
              limit: 50,
            }),
          );
        } else {
          return of([]);
        }
      }),
      tap((notifications) => {
        if (notifications?.length) {
          this.hasNewNotifications = notifications.filter((notification: any) => !notification.read).length;
        }
      }),
      catchError((error) => {
        this.logger.error(error, false);
        return of([]);
      }),
    );
  }
}
